import { CommonModule } from '@angular/common'
import { Component, ElementRef, ViewChild } from '@angular/core'
import { TranslateModule } from '@ngx-translate/core'
import { BehaviorSubject } from 'rxjs'
import { PowerState } from '../../interfaces/power-state'
import { PlatformService } from '../../platforms/platform.service'
import { ChannelPosition } from '../channel/channel-position'
import { LocalChannel } from '../../interfaces/local-channel'
import { LocalChannelLiveEPG } from '../../interfaces/local-epg'
import { CurrentChannelData } from '../../interfaces/channel-track-data'
import { ChannelResolution, ChannelType, UDPChannel } from '../../interfaces/channel'
import { environment } from '../../environments/environment'
import { ExternalInputType } from '../../services/external-input.service'

interface TestReport {
  name: string
  state: TestReportState
  logs: TestReportLog[]
}

interface TestReportLog {
  level: string
  msg: string
}

enum TestReportState {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

@Component({
  selector: 'app-test',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './test.component.html',
  styleUrls: ['./test.component.scss'],
})
export class TestComponent {
  reports: TestReport[] = []

  @ViewChild('avplayer') avplayer!: ElementRef

  constructor(private platformService: PlatformService) {
    // do nothing
  }

  restartApp(): void {
    window.location.href = 'index.html'
  }

  start(): void {
    this.resetReports()
    this.startReports()
  }

  createTestReport(name: string): TestReport {
    const report = {
      name: name,
      state: TestReportState.PENDING,
      logs: [],
    }
    this.reports.unshift(report)
    return report
  }

  logReport(report: TestReport, msg: string, level = 'info'): void {
    if (level == 'error') {
      console.error(`[${report.name}] ${msg}`)
    } else {
      console.log(`[${report.name}] ${msg}`)
    }
    report.logs.push({ level, msg })
  }

  resetReports(): void {
    this.reports = []
  }

  sleep(ms: number): Promise<void> {
    return new Promise(r => setTimeout(r, ms))
  }

  async startReports(): Promise<void> {
    try {
      await this.testInit()
      await this.sleep(200)

      this.testID()
      await this.sleep(200)

      this.testLocalStorage()
      await this.sleep(200)

      this.testMonitoringData()
      await this.sleep(200)

      this.testVolume()
      await this.sleep(200)

      await this.testMedia()
      await this.sleep(200)

      await this.testHDMI()
      await this.sleep(200)

      await this.testChannels()
      await this.sleep(200)

      await this.testApps()
      await this.sleep(200)

      await this.testPowerState()
      await this.sleep(200)
    } catch (err: any) {
      console.error(err)
    }
  }

  async testInit(): Promise<void> {
    const report = this.createTestReport('TestInit')
    report.state = TestReportState.RUNNING

    try {
      await this.platformService.init()
      report.state = TestReportState.SUCCESS
    } catch (err: any) {
      this.logReport(report, err.message, 'error')
      report.state = TestReportState.FAILURE
    }
  }

  testID(): void {
    const report = this.createTestReport('TestID')
    report.state = TestReportState.RUNNING

    try {
      const id = this.platformService.id()
      this.logReport(report, `id = ${id}`)
      report.state = TestReportState.SUCCESS
    } catch (err: any) {
      this.logReport(report, err.message, 'error')
      report.state = TestReportState.FAILURE
    }
  }

  async testMonitoringData(): Promise<void> {
    const report = this.createTestReport('TestMonitoringData')
    report.state = TestReportState.RUNNING

    const checkValue = (name: string, value?: any): boolean => {
      this.logReport(report, `${name} = ${value}`)
      if (value != undefined && typeof value === 'string' && value.length > 0) {
        return true
      } else {
        this.logReport(report, `Invalid value of ${name}: ${value}`, 'error')
        report.state = TestReportState.FAILURE
        return false
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const checkArray = (name: string, value?: any): boolean => {
      this.logReport(report, `${name} = ${value}`)
      if (value != undefined && typeof value === 'object') {
        return true
      } else {
        this.logReport(report, `Invalid value of ${name}: ${value}`, 'error')
        report.state = TestReportState.FAILURE
        return false
      }
    }

    try {
      const data = await this.platformService.getSystemData()
      console.log(data)

      const res =
        checkValue('model', data.model) &&
        checkValue('manufacturer', data.manufacturer) &&
        checkValue('serialNumber', data.serialNumber) &&
        checkValue('firmwareVersion', data.firmwareVersion) &&
        checkValue('appVersion', data.appVersion)

      report.state = res ? TestReportState.SUCCESS : TestReportState.FAILURE
    } catch (err: any) {
      this.logReport(report, err.message, 'error')
      report.state = TestReportState.FAILURE
    }
  }

  testLocalStorage(): void {
    const report = this.createTestReport('TestLocalStorage')
    report.state = TestReportState.RUNNING

    try {
      const testKey = 'testKey'
      const testValue = 'testValue'

      const initialValue = this.platformService.getValue(testKey)
      this.logReport(report, `Initial ${testKey} value: ${initialValue}`)

      this.platformService.setValue(testKey, testValue)
      this.logReport(report, `Set ${testKey} value to ${testValue}`)

      const getValue = this.platformService.getValue(testKey)
      this.logReport(report, `Get ${testKey} value: ${getValue}`)

      this.platformService.removeValue(testKey)
      this.logReport(report, `Removed ${testKey} value`)

      const removedValue = this.platformService.getValue(testKey)
      this.logReport(report, `Get ${testKey} value: ${removedValue}`)

      report.state = TestReportState.SUCCESS
    } catch (err: any) {
      this.logReport(report, err.message, 'error')
      report.state = TestReportState.FAILURE
    }
  }

  async testPowerState(): Promise<void> {
    const report = this.createTestReport('TestPowerState')
    report.state = TestReportState.RUNNING

    try {
      let powerState = await this.platformService.getPowerState()
      this.logReport(report, `Initial power state = ${powerState}`)

      this.logReport(report, `Registering power state listener`)
      const subject = new BehaviorSubject<PowerState | undefined>(powerState)
      subject.subscribe(newPowerState => {
        this.logReport(report, `Power state listener, new state = ${newPowerState}`)
        powerState = newPowerState
      })
      this.platformService.registerPowerStateListener(subject)
      await this.sleep(1000)

      this.logReport(report, `Set power state to NORMAL`)
      this.platformService.setPowerState(PowerState.NORMAL)
      await this.sleep(3000)
      if (powerState != PowerState.NORMAL) {
        this.logReport(report, `Invalid power state ${powerState}, should be PowerState.NORMAL`, 'error')
      }

      this.logReport(report, `Set power state to STANDBY`)
      this.platformService.setPowerState(PowerState.STANDBY)
      await this.sleep(3000)
      if (powerState != PowerState.STANDBY) {
        this.logReport(report, `Invalid power state ${powerState}, should be PowerState.STANDBY`, 'error')
      }

      this.logReport(report, `Set power state to NORMAL`)
      this.platformService.setPowerState(PowerState.NORMAL)
      await this.sleep(3000)
      if (powerState != PowerState.NORMAL) {
        this.logReport(report, `Invalid power state ${powerState}, should be PowerState.NORMAL`, 'error')
      }

      report.state = TestReportState.SUCCESS
    } catch (err: any) {
      this.logReport(report, err.message, 'error')
      report.state = TestReportState.FAILURE
    }
  }

  testVolume(): void {
    const report = this.createTestReport('TestVolume')
    report.state = TestReportState.RUNNING

    try {
      this.logReport(report, `getVolume: ${this.platformService.getVolume()}`)

      this.logReport(report, `setVolume(10)`)
      this.platformService.setVolume(10)
      this.logReport(report, `getVolume() = ${this.platformService.getVolume()}`)

      this.logReport(report, `setVolumeDown()`)
      this.platformService.setVolumeDown()
      this.logReport(report, `getVolume() = ${this.platformService.getVolume()}`)

      this.logReport(report, `setVolumeUp()`)
      this.platformService.setVolumeUp()
      this.logReport(report, `getVolume() = ${this.platformService.getVolume()}`)

      this.logReport(report, `getVolumeMute: ${this.platformService.getVolumeMute()}`)

      this.logReport(report, `setVolumeMute(true)`)
      this.platformService.setVolumeMute(true)
      this.logReport(report, `getVolumeMute: ${this.platformService.getVolumeMute()}`)

      this.logReport(report, `setVolumeMute(false)`)
      this.platformService.setVolumeMute(false)
      this.logReport(report, `getVolumeMute: ${this.platformService.getVolumeMute()}`)

      report.state = TestReportState.SUCCESS
    } catch (err: any) {
      this.logReport(report, err.message, 'error')
      report.state = TestReportState.FAILURE
    }
  }

  async testMedia(): Promise<void> {
    const report = this.createTestReport('TestMedia')
    report.state = TestReportState.RUNNING

    try {
      const media = {
        id: 'media-id',
        name: 'media-name',
        fileExtension: 'png',
        url: `${environment.serverUrl}/assets/images/logo.png`,
        sizeInBytes: 1812,
        sha1: 'sha1',
        md5: 'md5',
      }

      const localMedias1 = await this.platformService.getLocalMedias()
      this.logReport(report, `getLocalMedias() ${localMedias1.length} medias`)

      let localMediaFile = undefined
      try {
        this.logReport(report, `downloadMedia(${media.url})`)
        localMediaFile = await this.platformService.downloadMedia(media)
        this.logReport(report, `Media downloaded to ${localMediaFile.url}`)

        const localMedias2 = await this.platformService.getLocalMedias()
        this.logReport(report, `getLocalMedias() ${localMedias2.length} medias`)

        if (localMedias2.length !== localMedias1.length + 1) {
          this.logReport(report, `List of medias does not include the new downloaded medias`, 'error')
        }
      } catch (err: any) {
        this.logReport(report, 'Could not download media', 'error')
      }

      if (localMediaFile) {
        this.logReport(report, `removeMedia(${media.id})`)
        this.platformService.removeMedia(localMediaFile)

        const localMedias3 = await this.platformService.getLocalMedias()
        this.logReport(report, `getLocalMedias() ${localMedias3.length} medias`)

        if (localMedias3.length !== localMedias1.length) {
          this.logReport(report, `List of medias still has media that should be removed`, 'error')
        }
      } else {
        this.logReport(report, 'Cannot test removeMedia since no media was downloaded', 'error')
      }

      report.state = TestReportState.SUCCESS
    } catch (err: any) {
      const errMessage = err ? err.message : 'An error occurred'
      this.logReport(report, errMessage, 'error')
      report.state = TestReportState.FAILURE
    }
  }

  async testHDMI(): Promise<void> {
    const report = this.createTestReport('TestHDMI')
    report.state = TestReportState.RUNNING

    try {
      const externalInput = {
        id: 'HDMI-1',
        type: ExternalInputType.HDMI,
        index: 0,
        number: 1,
        connected: true,
      }
      this.logReport(report, `Starting HDMI on port ${externalInput.number}`)
      this.platformService.showHDMI(externalInput)
      await this.sleep(3000)

      this.logReport(report, `Stopping HDMI`)
      this.platformService.hideHDMI()

      report.state = TestReportState.SUCCESS
    } catch (err: any) {
      const errMessage = err ? err.message : 'An error occurred'
      this.logReport(report, errMessage, 'error')
      report.state = TestReportState.FAILURE
    }
  }

  async testChannels(): Promise<void> {
    const report = this.createTestReport('TestChannels')
    report.state = TestReportState.RUNNING

    try {
      const localChannel: LocalChannel = {
        id: 'channel-id',
        globalChannelId: 'global-channel-id',
        name: 'channel-name',
        type: ChannelType.UDP,
        source: {
          name: 'channel-source-name',
          configuredChannelType: ChannelType.UDP,
          channelNumber: 1,
          planTypes: [],
          purchasable: false,
          resolution: ChannelResolution.HD,
          isRecordable: false,
          isPauseLiveTvEnabled: false,
          globalChannelId: 'global-channel-id',
          ip: '239.192.192.1',
          port: 1234,
          serviceId: 1,
          rtp: false,
          drm: false,
        } as UDPChannel,
        logoUrl: 'channel-logo-url',
        channelData$: new BehaviorSubject<CurrentChannelData | undefined>(undefined),
        liveEPG$: new BehaviorSubject<LocalChannelLiveEPG | undefined>(undefined),
      }
      const boundingRect: DOMRect = this.avplayer.nativeElement.getBoundingClientRect()
      const pos: ChannelPosition = {
        left: boundingRect.left,
        top: boundingRect.top,
        width: boundingRect.width,
        height: boundingRect.height,
      }

      this.logReport(report, `Starting channel`)
      this.platformService.startUDPChannel(localChannel, pos)

      this.logReport(report, `Polling channel data`)
      const timer = setInterval(async () => {
        this.logReport(report, `Polling...`)
        const data = await this.platformService.getUDPCurrentProgramData()
        if (data) {
          this.logReport(
            report,
            `Found ${data?.audioTracks.length} audio tracks and ${data?.subtitleTracks.length} subtitle tracks`
          )

          this.logReport(report, `Changing audio track`)
          const newAudioTrack = data?.audioTracks.filter(track => track.index != data?.currentAudioTrack?.index)[0]
          if (newAudioTrack) {
            this.logReport(report, `setAudioTrack(${newAudioTrack.index})`)
            this.platformService.setUDPAudioTrack(newAudioTrack)
          }

          const newSubtitleTrack = data?.subtitleTracks.filter(
            track => track.index != data?.currentSubtitleTrack?.index
          )[0]
          if (newSubtitleTrack) {
            this.logReport(report, `setSubtitleTrack(${newSubtitleTrack.index})`)
            this.platformService.setUDPSubtitleTrack(newSubtitleTrack)
          }

          this.logReport(report, `setSubtitleState(true})`)
          this.platformService.setSubtitleState(true)

          clearTimeout(timer)
        }
      }, 1000)

      await this.sleep(10000)
      clearTimeout(timer)

      this.logReport(report, `Stopping channel`)
      this.platformService.stopUDPChannel()

      report.state = TestReportState.SUCCESS
    } catch (err: any) {
      const errMessage = err ? err.message : 'An error occurred'
      this.logReport(report, errMessage, 'error')
      report.state = TestReportState.FAILURE
    }
  }

  async testApps(): Promise<void> {
    const report = this.createTestReport('TestApps')
    report.state = TestReportState.RUNNING

    try {
      const apps = await this.platformService.getAppList()
      this.logReport(report, `Found ${apps.length} apps`)

      this.logReport(report, `TODO: startApp('appId')`)
      //this.platformService.startApp('appId')

      report.state = TestReportState.SUCCESS
    } catch (err: any) {
      const errMessage = err ? err.message : 'An error occurred'
      this.logReport(report, errMessage, 'error')
      report.state = TestReportState.FAILURE
    }
  }
}
